// Triage mode (P12): pure logic. No I/O. See docs/plans/2026-10-26-P12-triage-mode.md.
//
// Shaun stages decisions here; the browser keeps them (triage-store.ts) until he sends a batch.
// bee saves the batch; Hal on `work` applies it. Every rule below is checked again on bee and on `work`.

import type {
  TriageAction,
  TriageBatchInput,
  TriageCandidate,
  TriageDecisionInput,
  TriageLabelKind,
  TriageView,
} from "./api";

/** The queue a card sits in. "todo" = likely-done + stale + unlabeled, the default view. */
export type Bucket = "likely-done" | "stale" | "unlabeled" | "live" | "waiting" | "snoozed";
export type QueueFilter = "todo" | Bucket;

export const BUCKET_LABEL: Record<Bucket, string> = {
  "likely-done": "likely done",
  stale: "stale",
  unlabeled: "unlabeled",
  live: "live",
  waiting: "waiting",
  snoozed: "snoozed",
};

export const ACTION_LABEL: Record<TriageAction, string> = {
  done: "Done",
  drop: "Drop",
  keep: "Keep",
  snooze: "Snooze",
  reschedule: "Reschedule",
  note: "Note",
};

/** Short past-tense label for the undo snackbar: "Dropped · Undo". */
export const STAGED_LABEL: Record<TriageAction, string> = {
  done: "Marked done",
  drop: "Dropped",
  keep: "Kept",
  snooze: "Snoozed",
  reschedule: "Rescheduled",
  note: "Note added",
};

export const ACTION_KEY: Record<TriageAction, string> = {
  done: "d",
  drop: "x",
  keep: "k",
  snooze: "s",
  reschedule: "r",
  note: "n",
};

export interface Staged {
  taskId: string;
  action: TriageAction;
  date?: string;
  fromDue?: string;
  reason?: string;
  note?: string;
  /** done/drop on a parent: apply to its open children too */
  includeChildren?: boolean;
  stagedAt: string;
  /** What the card showed when it was staged. The payload is built from this, not from a later projection. */
  snapshot: {
    path: string;
    raw: string;
    hash: string;
    text: string;
    projectName: string;
    domain: TriageCandidate["domain"];
    children: Array<{ raw: string; hash: string }>;
    label?: { label: string; confidence?: string; generatedAt?: string };
  };
}

/** Effective label: Mockingbird is never "stale" (personal cadence), whatever the file says. */
export function labelOf(c: TriageCandidate): TriageLabelKind | null {
  const l = c.label?.label ?? null;
  if (l === "stale" && c.domain === "mkbd") return null;
  return l;
}

export function bucketOf(c: TriageCandidate, today: string): Bucket {
  if (c.remindOn && c.remindOn > today && c.dueCount === 0) return "snoozed";
  if (c.waiting) return "waiting";
  const l = labelOf(c);
  if (l === "waiting") return "waiting";
  if (l === "live") return "live";
  if (l === "likely-done") return "likely-done";
  if (l === "stale") return "stale";
  return "unlabeled";
}

export function inFilter(bucket: Bucket, filter: QueueFilter): boolean {
  if (filter === "todo")
    return bucket === "likely-done" || bucket === "stale" || bucket === "unlabeled";
  return bucket === filter;
}

const RANK: Record<Bucket, number> = {
  "likely-done": 0,
  stale: 1,
  unlabeled: 2,
  live: 3,
  waiting: 4,
  snoozed: 5,
};

/** Strong likely-done first, then weak, then stale, unlabeled …; within a rank by project, then line. */
export function compareCards(a: TriageCandidate, b: TriageCandidate, today: string): number {
  const ra = RANK[bucketOf(a, today)] * 2 + (a.label?.confidence === "strong" ? 0 : 1);
  const rb = RANK[bucketOf(b, today)] * 2 + (b.label?.confidence === "strong" ? 0 : 1);
  if (ra !== rb) return ra - rb;
  return a.projectName.localeCompare(b.projectName) || a.line - b.line;
}

/**
 * Ids that are out of the queue and the counts: staged in this browser, sent from it,
 * in a pending batch, or settled in an applied batch.
 */
export function hiddenIds(
  view: TriageView,
  sent: Iterable<string>,
  staged: Iterable<string> = []
): Set<string> {
  const out = new Set<string>([...view.pending, ...sent, ...staged]);
  for (const [id, r] of Object.entries(view.recent)) {
    if (r.outcome !== "skipped") out.add(id);
  }
  return out;
}

export interface QueueOptions {
  filter: QueueFilter;
  domain: TriageCandidate["domain"] | "all";
  today: string;
  sent: Iterable<string>;
  /** Staged in this browser: out of the queue and the counts at once, back on undo or unstage. */
  staged?: Iterable<string>;
  /** "priority" (default): likely-done first. "oldest": by added date, unknown last. */
  order?: QueueOrder;
}

export type QueueOrder = "priority" | "oldest";

/** Oldest added first. Unknown dates go last, then the priority order breaks ties. */
export function compareOldest(a: TriageCandidate, b: TriageCandidate, today: string): number {
  const da = a.added ?? "9999-99-99";
  const db = b.added ?? "9999-99-99";
  if (da !== db) return da < db ? -1 : 1;
  return compareCards(a, b, today);
}

/** "added Sep 28", or "added: unknown", plus a tooltip that names the source. */
export function addedText(
  c: TriageCandidate,
  dayLabel: (d: string) => string
): { text: string; title: string } {
  if (!c.added || c.addedSource === "unknown" || !c.addedSource) {
    return {
      text: "added: unknown",
      title:
        "Not found in Hal's session history, and older than bee's tracking (started 2026-10-26).",
    };
  }
  return c.addedSource === "history"
    ? {
        text: `added ${dayLabel(c.added)}`,
        title: `From Hal's session history: the earliest session that wrote this line (${c.added}). It may be older.`,
      }
    : {
        text: `added ${dayLabel(c.added)}`,
        title: `First seen by bee's vault-reader on ${c.added}.`,
      };
}

export function buildQueue(view: TriageView, opts: QueueOptions): TriageCandidate[] {
  const hidden = hiddenIds(view, opts.sent, opts.staged);
  return view.candidates
    .filter((c) => !hidden.has(c.id))
    .filter((c) => opts.domain === "all" || c.domain === opts.domain)
    .filter((c) => inFilter(bucketOf(c, opts.today), opts.filter))
    .sort((a, b) =>
      opts.order === "oldest" ? compareOldest(a, b, opts.today) : compareCards(a, b, opts.today)
    );
}

export function bucketCounts(
  view: TriageView,
  opts: Omit<QueueOptions, "filter">
): Record<QueueFilter, number> {
  const hidden = hiddenIds(view, opts.sent, opts.staged);
  const counts: Record<QueueFilter, number> = {
    todo: 0,
    "likely-done": 0,
    stale: 0,
    unlabeled: 0,
    live: 0,
    waiting: 0,
    snoozed: 0,
  };
  for (const c of view.candidates) {
    if (hidden.has(c.id)) continue;
    if (opts.domain !== "all" && c.domain !== opts.domain) continue;
    const b = bucketOf(c, opts.today);
    counts[b]++;
    if (inFilter(b, "todo")) counts.todo++;
  }
  return counts;
}

/** Which buttons a card offers. The same rules hold on bee (validateBatch) and on `work` (triage-apply.py). */
export function allowedActions(c: TriageCandidate): Record<TriageAction, boolean> {
  return {
    done: true,
    drop: true,
    keep: true,
    snooze: c.dueCount === 0, // only tasks without a real deadline
    reschedule: c.dueCount === 1, // only a task that already has one real deadline
    note: true,
  };
}

/** Snooze, Reschedule and Note need input. Done or Drop on a task with open sub-tasks asks about them. */
export function needsPanel(c: TriageCandidate, action: TriageAction): boolean {
  return (
    action === "snooze" ||
    action === "reschedule" ||
    action === "note" ||
    ((action === "done" || action === "drop") && c.children.length > 0)
  );
}

export interface StageInput {
  action: TriageAction;
  date?: string;
  reason?: string;
  note?: string;
  includeChildren?: boolean;
}

/** Validate one decision against its card. Returns an error message, or null when it can be staged. */
export function stageError(c: TriageCandidate, input: StageInput): string | null {
  const allowed = allowedActions(c);
  if (!allowed[input.action]) {
    return input.action === "snooze"
      ? "This task has a deadline. Reschedule it instead."
      : "Reschedule needs a task with one deadline.";
  }
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if ((input.action === "snooze" || input.action === "reschedule") && !day.test(input.date ?? "")) {
    return "Pick a date.";
  }
  if (input.action === "reschedule" && !input.reason?.trim()) return "Reschedule needs a reason.";
  if (input.action === "note" && !input.note?.trim()) return "Write the note.";
  if ((input.note?.length ?? 0) > 2000) return "Notes are limited to 2,000 characters.";
  if ((input.reason?.length ?? 0) > 500) return "Reasons are limited to 500 characters.";
  return null;
}

export function makeStaged(c: TriageCandidate, input: StageInput, now = new Date()): Staged {
  return {
    taskId: c.id,
    action: input.action,
    date: input.action === "snooze" || input.action === "reschedule" ? input.date : undefined,
    fromDue: input.action === "reschedule" ? c.due : undefined,
    reason: input.reason?.trim() || undefined,
    note: input.note?.trim() || undefined,
    includeChildren:
      (input.action === "done" || input.action === "drop") && c.children.length > 0
        ? Boolean(input.includeChildren)
        : undefined,
    stagedAt: now.toISOString(),
    snapshot: {
      path: c.path,
      raw: c.raw,
      hash: c.hash,
      text: c.text,
      projectName: c.projectName,
      domain: c.domain,
      children: c.children.map((ch) => ({ raw: ch.raw, hash: ch.hash })),
      label:
        c.label && labelOf(c)
          ? { label: c.label.label, confidence: c.label.confidence, generatedAt: undefined }
          : undefined,
    },
  };
}

/** A staged decision whose task text changed since (its id is gone) cannot be sent. */
export function isStale(s: Staged, view: TriageView | null): boolean {
  if (!view) return false;
  return !view.candidates.some((c) => c.id === s.taskId && c.hash === s.snapshot.hash);
}

export function toDecision(s: Staged, labelsAt?: string | null): TriageDecisionInput {
  const d: TriageDecisionInput = {
    taskId: s.taskId,
    path: s.snapshot.path,
    raw: s.snapshot.raw,
    hash: s.snapshot.hash,
    action: s.action,
    stagedAt: s.stagedAt,
  };
  if (s.date) d.date = s.date;
  if (s.fromDue) d.fromDue = s.fromDue;
  if (s.reason) d.reason = s.reason;
  if (s.note) d.note = s.note;
  if (s.includeChildren && s.snapshot.children.length) d.children = s.snapshot.children;
  if (s.snapshot.label) d.label = { ...s.snapshot.label, generatedAt: labelsAt ?? undefined };
  return d;
}

export function buildBatch(
  staged: Staged[],
  view: TriageView,
  clientBatchId: string
): TriageBatchInput {
  return {
    schema: 1,
    clientBatchId,
    seen: { projectionAt: view.generatedAt, labelsAt: view.labelsAt ?? undefined },
    decisions: staged.filter((s) => !isStale(s, view)).map((s) => toDecision(s, view.labelsAt)),
  };
}

/** "Stage all N": strong likely-done cards whose every evidence item has a link. */
export function groupStageable(cards: TriageCandidate[], today: string): TriageCandidate[] {
  return cards.filter(
    (c) =>
      bucketOf(c, today) === "likely-done" &&
      c.label?.confidence === "strong" &&
      c.label.evidence.length > 0 &&
      c.label.evidence.every((e) => Boolean(e.url))
  );
}

/** Snooze presets from today: +1 week, +2 weeks, +1 month. */
export function snoozePresets(today: string): Array<{ label: string; date: string }> {
  const at = (days: number) => {
    const d = new Date(`${today}T00:00:00`);
    d.setDate(d.getDate() + days);
    return localDay(d);
  };
  const month = new Date(`${today}T00:00:00`);
  month.setMonth(month.getMonth() + 1);
  return [
    { label: "+1 week", date: at(7) },
    { label: "+2 weeks", date: at(14) },
    { label: "+1 month", date: localDay(month) },
  ];
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Summary line for the tray, e.g. "Done 4 · Drop 2 · Snooze 1". */
export function stagedSummary(staged: Staged[]): string {
  const counts = new Map<TriageAction, number>();
  for (const s of staged) counts.set(s.action, (counts.get(s.action) ?? 0) + 1);
  return (Object.keys(ACTION_LABEL) as TriageAction[])
    .filter((a) => counts.has(a))
    .map((a) => `${ACTION_LABEL[a]} ${counts.get(a)}`)
    .join(" · ");
}

// --- Undo: the last staging action can be reverted for a few seconds ---

export const UNDO_MS = 5_000;

export interface UndoEntry {
  /** The task ids this action staged. One for a card, several for "Stage all N". */
  ids: string[];
  /** What each id held before: a re-staged card goes back to its old decision, a new one is removed. */
  previous: Record<string, Staged | undefined>;
  /** Shown in the toast, e.g. "Done · Merge DS #241". */
  label: string;
  at: number;
}

/** Stage one or more decisions. Returns the new items and the entry that undoes exactly this step. */
export function applyStage(
  items: Record<string, Staged>,
  entries: Staged[],
  label: string,
  now = Date.now()
): { items: Record<string, Staged>; undo: UndoEntry } {
  const next = { ...items };
  const previous: Record<string, Staged | undefined> = {};
  for (const e of entries) {
    previous[e.taskId] = items[e.taskId];
    next[e.taskId] = e;
  }
  return { items: next, undo: { ids: entries.map((e) => e.taskId), previous, label, at: now } };
}

/** Revert one staging step. */
export function applyUndo(items: Record<string, Staged>, undo: UndoEntry): Record<string, Staged> {
  const next = { ...items };
  for (const id of undo.ids) {
    const prev = undo.previous[id];
    if (prev) next[id] = prev;
    else delete next[id];
  }
  return next;
}

export function undoLeft(undo: UndoEntry | null, now = Date.now()): number {
  if (!undo) return 0;
  return Math.max(0, UNDO_MS - (now - undo.at));
}

/** The queue to show a card in: the current one if it fits, else the card's own bucket. */
export function filterFor(c: TriageCandidate, today: string, current: QueueFilter): QueueFilter {
  const b = bucketOf(c, today);
  return inFilter(b, current) ? current : b;
}
